import { useMemo } from 'react'
import { CheckCircle, XCircle, AlertTriangle, Loader2 } from 'lucide-react'
import { parse as parseYaml } from 'yaml'
import { Badge } from '@/components/ui/badge'
import type { WorkflowValidationResult } from '@/types'

interface YamlValidationPanelProps {
  yaml: string
  backendValidation: WorkflowValidationResult | undefined
  isValidating: boolean
  onGoToLine: (line: number) => void
}

interface ClientValidationResult {
  isValid: boolean
  errors: { message: string; line: number }[]
}

export function validateYaml(yamlContent: string): ClientValidationResult {
  if (!yamlContent.trim()) {
    return { isValid: false, errors: [{ message: 'YAML content is required', line: 1 }] }
  }
  try {
    parseYaml(yamlContent)
    return { isValid: true, errors: [] }
  } catch (err: unknown) {
    if (err instanceof Error) {
      const yamlError = err as { linePos?: Array<{ line: number }> }
      const line = yamlError.linePos?.[0]?.line ?? 1
      return { isValid: false, errors: [{ message: err.message, line }] }
    }
    return { isValid: false, errors: [{ message: 'Unknown YAML error', line: 1 }] }
  }
}

const LAYER_COLORS: Record<string, string> = {
  Syntax: 'bg-danger text-danger-foreground',
  Schema: 'bg-new text-new-foreground',
  Structural: 'bg-warning text-warning-foreground',
  References: 'bg-info text-info-foreground',
  Semantic: 'bg-purple text-purple-foreground',
}

export function YamlValidationPanel({ yaml, backendValidation, isValidating, onGoToLine }: YamlValidationPanelProps) {
  const clientValidation = useMemo(() => validateYaml(yaml), [yaml])

  const hasSyntaxErrors = !clientValidation.isValid

  const isValid = hasSyntaxErrors ? false : (backendValidation?.isValid ?? true)
  const errorCount = hasSyntaxErrors
    ? clientValidation.errors.length
    : (backendValidation?.errors.length ?? 0)
  const warningCount = backendValidation?.warnings.length ?? 0

  return (
    <div data-testid="yaml-validation-status">
      <div className="flex items-center justify-between p-4 border-b border-border">
        <h4 className="text-lg font-bold leading-[1.4] tracking-[0.015em] text-card-foreground">Validation</h4>
        <div className="flex items-center gap-1.5">
          {isValidating && (
            <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
          )}
          {isValid && !isValidating ? (
            <Badge className="bg-success text-success-foreground hover:bg-success border-success-foreground rounded-full px-2.5 h-6">
              <CheckCircle className="mr-1 h-3 w-3" />
              Valid
            </Badge>
          ) : errorCount > 0 ? (
            <Badge className="bg-danger text-danger-foreground border-danger-foreground hover:bg-danger rounded-full px-2.5 h-6">
              <XCircle className="mr-1 h-3 w-3" />
              {errorCount} {errorCount === 1 ? 'error' : 'errors'}
            </Badge>
          ) : null}
          {warningCount > 0 && (
            <Badge className="bg-warning text-warning-foreground hover:bg-warning rounded-full px-2.5 h-6">
              <AlertTriangle className="mr-1 h-3 w-3" />
              {warningCount}
            </Badge>
          )}
        </div>
      </div>
      <div className="p-4 space-y-3">
        {hasSyntaxErrors && (
          <div className="space-y-2">
            {clientValidation.errors.map((error, i) => (
              <button
                key={`syntax-${i}`}
                className="block w-full text-left text-xs text-danger-foreground bg-danger rounded-lg p-3 hover:bg-danger-foreground/10 transition-colors cursor-pointer"
                onClick={() => onGoToLine(error.line)}
                title="Click to jump to error line"
              >
                {error.message.split('\n')[0]}
              </button>
            ))}
          </div>
        )}

        {!hasSyntaxErrors && backendValidation && backendValidation.errors.length > 0 && (
          <div className="space-y-2">
            {backendValidation.errors.map((error, i) => (
              <button
                key={`error-${i}`}
                className="block w-full text-left text-xs text-danger-foreground bg-danger rounded-lg p-3 hover:bg-danger-foreground/10 transition-colors cursor-pointer"
                onClick={() => error.lineNumber ? onGoToLine(error.lineNumber) : undefined}
                title={error.lineNumber ? 'Click to jump to error line' : undefined}
              >
                <span className="flex items-center gap-2">
                  <Badge className={`text-[10px] px-1.5 py-0 h-4 shrink-0 ${LAYER_COLORS[error.layer] ?? 'bg-neutral text-neutral-foreground'}`}>
                    {error.layer}
                  </Badge>
                  <span>{error.message}</span>
                  {error.lineNumber && (
                    <span className="ml-auto shrink-0 text-danger-foreground">L{error.lineNumber}</span>
                  )}
                </span>
              </button>
            ))}
          </div>
        )}

        {!hasSyntaxErrors && backendValidation && backendValidation.warnings.length > 0 && (
          <div className="space-y-2">
            {backendValidation.warnings.map((warning, i) => (
              <button
                key={`warn-${i}`}
                className="block w-full text-left text-xs text-warning-foreground bg-warning rounded-lg p-3 hover:bg-warning transition-colors cursor-pointer"
                onClick={() => warning.lineNumber ? onGoToLine(warning.lineNumber) : undefined}
                title={warning.lineNumber ? 'Click to jump to line' : undefined}
              >
                <span className="flex items-center gap-2">
                  <AlertTriangle className="h-3 w-3 shrink-0" />
                  <span>{warning.message}</span>
                  {warning.lineNumber && (
                    <span className="ml-auto shrink-0 text-warning">L{warning.lineNumber}</span>
                  )}
                </span>
              </button>
            ))}
          </div>
        )}

        {isValid && !isValidating && !hasSyntaxErrors && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <CheckCircle className="h-4 w-4 shrink-0 text-success-foreground" />
            <span>YAML is valid</span>
          </div>
        )}

        {isValidating && !hasSyntaxErrors && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>Validating...</span>
          </div>
        )}
      </div>
    </div>
  )
}
